import React, { useState } from 'react';
import { useData } from '../../context/DataContext';

export const AchievementMatrixTable = ({ onCellClick }) => {
  const { getScopedStudents } = useData();
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [expandedRows, setExpandedRows] = useState({});

  const scoped = getScopedStudents();

  // Find unique school-grade pairings in active scoped student list
  const schoolGradeKeys = [...new Set(scoped.map(s => `${s.school}|||${s.registeredGrade}`))]
    .map(key => {
      const [school, grade] = key.split('|||');
      return { school, grade: parseInt(grade) };
    })
    .sort((a, b) => a.school.localeCompare(b.school) || a.grade - b.grade);

  const uniqueGrades = [...new Set(scoped.map(s => s.registeredGrade))].sort((a, b) => a - b);

  const toggleRow = (rowKey) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowKey]: !prev[rowKey]
    }));
  };

  const computeGapDistribution = (students, subject) => {
   
    const dist = { atLevel: 0, yr1: 0, yr2: 0, yr3: 0, yr4: 0 };
    students.forEach(s => {
      if (!s.participated) return;
      const mastery = Math.floor(subject === 'math' ? s.mathMastery : s.englishMastery);
      const target =  s.registeredGrade;
      // const target = Math.min(7, s.registeredGrade);
      const gap = target - mastery;
      
      if (gap <= 0) dist.atLevel++;
      else if (gap === 1) dist.yr1++;
      else if (gap === 2) dist.yr2++;
      else if (gap === 3) dist.yr3++;
      else dist.yr4++;
    });
    return dist;
  };

  const COL_STYLE = {
    atLevel: { bg: '#e8f5ee', txt: '#2a6a4a' },
    yr1:     { bg: '#fff8e1', txt: '#d4870a' },
    yr2:     { bg: '#fff8e1', txt: '#e09020' },
    yr3:     { bg: '#fff0f0', txt: '#dc3545' },
    yr4:     { bg: '#ffe8e8', txt: '#c0392b' }
  };

  const renderCell = (count, key, label, subjectName, school, gradeNum, section = "Unassigned") => {
    if (count === 0) {
      return <td className="p-2 border text-center text-gray-400 text-sm font-bold">—</td>;
    }
    const s = COL_STYLE[key];
    return (
      <td
        onClick={() => onCellClick({ grade: gradeNum, gapType: label, subject: subjectName, school, section })}
        className="p-2 border text-center font-bold text-base cursor-pointer hover:opacity-80 transition-opacity"
        style={{ background: s.bg, color: s.txt }}
      >
        {count}
      </td>
    );
  };

  const renderSubjectTable = (subjectKey, subjectName, color) => {
    const visible = schoolGradeKeys.filter(
      item => selectedGrade === 'all' || item.grade === parseInt(selectedGrade)
    );

    let prevSchool = '';
    const rows = [];
    visible.forEach((item, idx) => {
      const isNewSchool = item.school !== prevSchool;
      if (isNewSchool) prevSchool = item.school;
      const schoolGroup = visible.filter(g => g.school === item.school);
      rows.push({
        ...item,
        isNewSchool,
        rowspan: schoolGroup.length,
        idx
      });
    });

    return (
      <div>
        <h4 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: color }}>
          <span className="w-3 h-3 rounded-full" style={{ background: color }}></span> {subjectName}
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs text-center">
            <thead>
              <tr className="text-white text-xs font-bold">
                <th className="p-2 border text-left" style={{ background: '#1e3a5f' }}>SCHOOL</th>
                <th className="p-2 border text-left" style={{ background: '#1e3a5f' }}>GRADE</th>
                <th className="p-2 border" style={{ background: '#2a6a4a' }}>AT GRADE LEVEL</th>
                <th className="p-2 border" style={{ background: '#d4870a' }}>1 YR BELOW</th>
                <th className="p-2 border" style={{ background: '#e09020' }}>2 YRS BELOW</th>
                <th className="p-2 border" style={{ background: '#dc3545' }}>3 YRS BELOW</th>
                <th className="p-2 border" style={{ background: '#c0392b' }}>4 YRS BELOW</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const rowKey = `${subjectKey}-${row.school}-${row.grade}`;
                const isOpen = !!expandedRows[rowKey];
                const groupStudents = scoped.filter(
                  s => s.school === row.school && s.registeredGrade === row.grade
                );
                const dist = computeGapDistribution(groupStudents, subjectKey);
                const sections = [...new Set(groupStudents.map(s => s.section).filter(Boolean))].sort();
                return (
                  <React.Fragment key={rowKey}>
                    <tr className="border-b hover:bg-gray-50">
                      {row.isNewSchool && (
                        <td
                          rowSpan={row.rowspan +100}
                          className="p-2 border font-semibold text-left text-xs text-gray-700 align-top"
                        >
                          {row.school}
                        </td>
                      )}
                      <td
                        onClick={() => toggleRow(rowKey)}
                        className="p-2 border font-bold text-left text-xs cursor-pointer hover:opacity-80 transition-opacity"
                        style={{ color: '#1e3a5f' }}
                      >
                        <span className="inline-block mr-1">{isOpen ? '▾' : '▸'}</span>
                        Grade {row.grade}
                      </td>
                      {renderCell(dist.atLevel, 'atLevel', 'At Grade Level', subjectKey, row.school, row.grade)}
                      {renderCell(dist.yr1, 'yr1', '1 Yr Below', subjectKey, row.school, row.grade)}
                      {renderCell(dist.yr2, 'yr2', '2 Yrs Below', subjectKey, row.school, row.grade)}
                      {renderCell(dist.yr3, 'yr3', '3 Yrs Below', subjectKey, row.school, row.grade)}
                      {renderCell(dist.yr4, 'yr4', '4 Yrs Below', subjectKey, row.school, row.grade)}
                    </tr>

                    {/* Expandable Sections */}
                    {isOpen && sections.map((sec) => {
                      const secStudents = groupStudents.filter(s => s.section === sec);
                      const sDist = computeGapDistribution(secStudents, subjectKey);

                      return (
                        <tr key={sec} className="bg-gray-50 text-xs border-b border-gray-100">
                          <td className="p-2 border text-left pl-8 text-gray-500">
                            ↳ Section {sec}
                          </td>
                          {renderCell(sDist.atLevel, 'atLevel', 'At Grade Level', subjectKey, row.school, row.grade, sec)}
                          {renderCell(sDist.yr1, 'yr1', '1 Yr Below', subjectKey, row.school, row.grade, sec)}
                          {renderCell(sDist.yr2, 'yr2', '2 Yrs Below', subjectKey, row.school, row.grade, sec)}
                          {renderCell(sDist.yr3, 'yr3', '3 Yrs Below', subjectKey, row.school, row.grade, sec)}
                          {renderCell(sDist.yr4, 'yr4', '4 Yrs Below', subjectKey, row.school, row.grade, sec)}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white p-6 rounded-2xl shadow-lg border-2 border-gray-100 mb-8 select-none">
      <div className="flex flex-wrap items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Achievement Level — By School &amp; Grade</h3>
          <p className="text-sm text-gray-600 mb-3">
            Student counts by achievement level, broken down by branch and registered grade.{' '}
            <span className="text-xs italic text-gray-500 font-bold">*Click any cell to view that student list.</span>
          </p>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 text-xs font-bold text-gray-700 bg-gray-50 p-2 rounded-lg border border-gray-100 w-fit">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ background: '#2a6a4a' }}></span>
              <span>At Grade Level</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ background: '#d4870a' }}></span>
              <span>1–2 Yrs Below</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ background: '#dc3545' }}></span>
              <span>3–4 Yrs Below</span>
            </div>
          </div>
          <p className="text-xs italic text-gray-400 mt-1.5">
            *A single weak level (60-79%) is forgiven if every other level is ≥80% — it still counts toward At Grade Level.
          </p>
        </div>
        <div className="flex gap-2">
          <select
            value={selectedGrade}
            onChange={(e) => setSelectedGrade(e.target.value)}
            className="px-3 py-2 border-2 border-gray-300 rounded-lg text-sm font-semibold text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none cursor-pointer"
          >
            <option value="all">All Grades</option>
            {uniqueGrades.map(g => (
              <option key={g} value={g}>
                Grade {g}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-8">
        {renderSubjectTable('english', 'English', '#2a6a4a')}
        {renderSubjectTable('math', 'Mathematics', '#1e3a9a')}
      </div>
    </div>
  );
};
export default AchievementMatrixTable;
